/**
 * LazySection.jsx - Intersection Observer Lazy Loading
 * 
 * Only renders children when the section scrolls into view.
 * Shows a skeleton placeholder until then.
 * 
 * Benefits:
 * - Faster initial page load
 * - Components don't make API calls until visible
 * - Smooth loading experience
 */
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

/**
 * Skeleton placeholder while content loads
 */
const SectionSkeleton = ({ height = 200, className = '' }) => (
    <div 
        className={`animate-pulse rounded-2xl ${className}`}
        style={{ 
            height: `${height}px`,
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, rgba(212, 175, 55, 0.02) 100%)',
            border: '1px solid rgba(212, 175, 55, 0.1)'
        }}
    >
        <div className="h-full flex items-center justify-center">
            <div className="flex space-x-2">
                <div className="w-2 h-2 bg-primary/20 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-primary/20 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-primary/20 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
        </div>
    </div>
);

/**
 * LazySection - Renders children only when visible in viewport
 */
export const LazySection = ({ 
    children, 
    height = 200, 
    rootMargin = '100px',  // Start loading 100px before visible
    className = '',
    onVisible = null,
    delay = 0  // Optional delay before showing content
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    setIsVisible(true);
                    
                    // Optional delay for staggered loading effect
                    if (delay > 0) {
                        setTimeout(() => setShouldRender(true), delay);
                    } else {
                        setShouldRender(true);
                    }
                    
                    // Callback when section becomes visible
                    if (onVisible) onVisible();
                    
                    // Stop observing once visible
                    observer.disconnect();
                }
            },
            {
                rootMargin,
                threshold: 0.1
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, [isVisible, rootMargin, onVisible, delay]);

    return (
        <div ref={sectionRef} className={className}>
            {shouldRender ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                    {children}
                </motion.div>
            ) : (
                <SectionSkeleton height={height} />
            )}
        </div>
    );
};

export default LazySection;
